import { useState } from "react";
import { Link } from "react-router-dom";
import { CheckCircle2, Loader2, MailQuestion } from "lucide-react";
import api, { PLATFORM_NAME, errMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      await api.post("/auth/forgot-password", { email: email.trim().toLowerCase() });
      setSent(true);
    } catch (err) {
      setError(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-ink-900 px-4 text-slate-200">
      <div className="w-full max-w-md rise">
        <Link to="/" className="flex items-center gap-2.5 font-display text-lg font-semibold">
          <img src="/limolinkx-logo.png" alt="" className="h-8 w-8 rounded bg-white/95 object-contain p-0.5" />
          <span className="gold-text">{PLATFORM_NAME}</span>
        </Link>

        {sent ? (
          <div data-testid="forgot-sent" className="mt-8 rounded-xl border border-ink-600 bg-ink-800/60 p-8">
            <CheckCircle2 className="h-6 w-6 text-emerald-400" />
            <h1 className="mt-4 text-2xl font-bold text-slate-50">Check your email</h1>
            <p className="mt-3 text-sm text-slate-400">
              If <span className="text-slate-200">{email}</span> has an account, a reset link is on its way.
              It works once and expires in an hour.
            </p>
            <p className="mt-4 text-xs text-slate-500">
              Nothing arrived? Check spam, then{" "}
              <button data-testid="forgot-again" onClick={() => setSent(false)} className="text-gold underline">
                try another address
              </button>.
            </p>
          </div>
        ) : (
          <form onSubmit={submit} data-testid="forgot-form"
            className="mt-8 space-y-5 rounded-xl border border-ink-600 bg-ink-800/60 p-6 md:p-8">
            <MailQuestion className="h-6 w-6 text-gold" />
            <h1 className="text-2xl font-bold tracking-tight text-slate-50">Reset your password</h1>
            <p className="text-sm text-slate-400">
              Tell us the email on your account and we will send you a link to set a new password.
            </p>
            <div>
              <label className="mb-2 block font-mono text-xs uppercase tracking-widest text-gold">Email</label>
              <Input data-testid="forgot-email" type="email" required value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="h-12 border-ink-600 bg-ink-800 text-slate-100 focus-visible:ring-gold" />
            </div>
            {error && <p data-testid="forgot-error" className="text-sm text-red-400">{error}</p>}
            <Button data-testid="forgot-submit" type="submit" disabled={busy}
              className="h-12 w-full bg-gold text-ink-900 hover:bg-gold-light">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Send the reset link"}
            </Button>
            <p className="text-center text-sm text-slate-500">
              <Link to="/login" data-testid="forgot-back" className="text-gold">Back to sign in</Link>
            </p>
          </form>
        )}
      </div>
    </div>
  );
}
